import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, ArrowLeft, Share2, Twitter, Facebook, Linkedin, Link as LinkIcon, Check, Heart, FileText, Map, Brain, Lock, Download } from "lucide-react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import DOMPurify from "dompurify";
import { Quiz } from "@/components/Quiz";
import { User } from "@supabase/supabase-js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";
import { PremiumUnlockGate } from "@/components/PremiumUnlockGate";
import { useUnlock } from "@/hooks/useUnlock";
import { usePremiumProgressTracker } from "@/hooks/usePremiumProgressTracker";

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category_id: string;
  featured_image: string | null;
  video_url: string | null;
  read_time: string;
  status: string;
  published_at: string | null;
  resource_url: string | null;
  resource_name: string | null;
  categories: {
    name: string;
    color: string;
  } | null;
}

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface RoadmapWeek {
  week: number;
  title: string;
  days: Array<{
    day: number;
    title: string;
    tasks: string[];
  }>;
}

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostData[]>([]);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [quizId, setQuizId] = useState<string | null>(null);
  const [roadmap, setRoadmap] = useState<RoadmapWeek[]>([]);
  const [roadmapId, setRoadmapId] = useState<string | null>(null);
  const [premiumResources, setPremiumResources] = useState<Array<{
    id: string;
    name: string;
    description: string | null;
    file_url: string;
    file_format: string | null;
    file_size: string | null;
  }>>([]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");
  const { toast } = useToast();

  const { isUnlocked } = useUnlock(user, "premium_guide", post?.id ?? null);

  usePremiumProgressTracker({
    user,
    enabled: isUnlocked && activeTab === "premium" && !!post?.id,
    contentType: "premium_guide",
    entityId: post?.id ?? null,
    title: post?.title,
    slug: post?.slug,
    featuredImage: post?.featured_image,
    contentSelector: "#premium-content-region",
  });

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || '';
  const shareText = post?.excerpt || '';

  const trackShare = async (platform: 'twitter' | 'facebook' | 'linkedin' | 'copy' | 'native') => {
    if (!post?.id) return;

    try {
      await supabase.from('article_shares').insert({
        blog_post_id: post.id,
        platform,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      });
    } catch (error) {
      console.error('Error tracking share:', error);
    }
  };

  const handleShare = async (platform: 'twitter' | 'facebook' | 'linkedin' | 'copy' | 'native') => {
    if (platform === 'copy') {
      try {
        // Copy FIRST - must be synchronous with user gesture on iOS
        await navigator.clipboard.writeText(shareUrl);
        
        // Track AFTER successful copy (don't await to keep UI responsive)
        trackShare(platform).catch(err => console.error('Error tracking share:', err));
        
        setCopied(true);
        toast({
          title: "Link copied!",
          description: "The article link has been copied to your clipboard.",
        });
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Clipboard error:', err);
        toast({
          title: "Failed to copy",
          description: "Please try again.",
          variant: "destructive",
        });
      }
      return;
    }

    // For other platforms, track first since they don't have the same restriction
    await trackShare(platform);

    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.error('Native share error:', err);
      }
      return;
    }

    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    };

    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const fetchLikes = async (postId: string) => {
    try {
      const { count } = await supabase
        .from('blog_post_likes')
        .select('*', { count: 'exact', head: true })
        .eq('blog_post_id', postId);

      setLikeCount(count || 0);
    } catch (error) {
      console.error('Error fetching likes:', error);
    }
  };

  const handleLike = async () => {
    if (!post?.id) return;

    try {
      // Simply insert a new like - no checks, no toggle
      await supabase
        .from('blog_post_likes')
        .insert({ blog_post_id: post.id });

      // Increment count immediately for instant feedback
      setLikeCount(prev => prev + 1);
      
      toast({
        title: "❤️ Liked!",
        description: "Thanks for your support!",
      });
    } catch (error) {
      console.error('Error adding like:', error);
      toast({
        title: "Error",
        description: "Failed to add like. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;

      try {
        // Fetch the post with category info
        const { data, error } = await supabase
          .from("blog_posts")
          .select(`
            *,
            categories (
              name,
              color
            )
          `)
          .eq("slug", slug)
          .eq("status", "published")
          .maybeSingle();

        if (error) throw error;
        
        if (data) {
          setPost(data);
          
          // Track the page view
          try {
            await supabase.from('article_views').insert({
              blog_post_id: data.id,
              user_agent: navigator.userAgent,
              referrer: document.referrer || null,
            });
          } catch (err) {
            console.error('Error tracking view:', err);
          }
          
          // Fetch likes
          await fetchLikes(data.id);

          // Fetch roadmap
          const { data: roadmapData } = await (supabase as any)
            .from('roadmaps')
            .select('id, content')
            .eq('blog_post_id', data.id)
            .maybeSingle();

          if (roadmapData?.content && Array.isArray(roadmapData.content)) {
            setRoadmap(roadmapData.content as unknown as RoadmapWeek[]);
            setRoadmapId(roadmapData.id);
          }

          // Fetch premium guide resources for this post
          const { data: resourcesData } = await (supabase as any)
            .from('blog_post_resources')
            .select('id, name, description, file_url, file_format, file_size, order_index')
            .eq('blog_post_id', data.id)
            .order('order_index', { ascending: true });
          if (resourcesData) setPremiumResources(resourcesData);

          // Fetch quiz questions for this post
          const { data: quizData } = await supabase
            .from("quizzes")
            .select(`
              id,
              quiz_questions (
                question,
                options,
                correct_answer,
                explanation,
                order_index
              )
            `)
            .eq("blog_post_id", data.id)
            .maybeSingle();

          if (quizData?.quiz_questions) {
            setQuizId(quizData.id);
            const formattedQuestions = quizData.quiz_questions
              .sort((a: any, b: any) => a.order_index - b.order_index)
              .map((q: any) => ({
                question: q.question,
                options: q.options as string[],
                correctAnswer: q.correct_answer,
                explanation: q.explanation || "",
              }));
            setQuizQuestions(formattedQuestions);
          }
          
          // Fetch related posts from the same category
          const { data: related } = await supabase
            .from("blog_posts")
            .select(`
              *,
              categories (
                name,
                color
              )
            `)
            .eq("category_id", data.category_id)
            .eq("status", "published")
            .neq("id", data.id)
            .limit(3);
          
          setRelatedPosts(related || []);
        }
      } catch (error) {
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  // Listen to auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // If URL has #section-* hash, switch to premium tab and scroll there once loaded.
  useEffect(() => {
    if (loading || !post) return;
    const hash = window.location.hash.replace("#", "");
    if (!hash.startsWith("section-")) return;
    setActiveTab("premium");
    const t = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => window.clearTimeout(t);
  }, [loading, post?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="mb-4 text-4xl font-bold">Post Not Found</h1>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const metaImageUrl = post.featured_image || post.video_url || 'https://wisbquhnntaqwbxngmdw.supabase.co/storage/v1/object/public/blog-images/hero-blog.jpg';
  const metaDescription = post.excerpt || `Read ${post.title} on RunoBlog`;

  // JSON-LD Structured Data for Google Rich Snippets
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": metaDescription,
    "image": metaImageUrl,
    "datePublished": post.published_at,
    "dateModified": post.published_at,
    "author": {
      "@type": "Organization",
      "name": "RunoBlog",
      "url": "https://www.runoblog.site"
    },
    "publisher": {
      "@type": "Organization",
      "name": "RunoBlog",
      "url": "https://www.runoblog.site",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.runoblog.site/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": shareUrl
    },
    "articleSection": post.categories?.name || "Blog",
    "keywords": post.categories?.name || "",
    "wordCount": post.content.split(/\s+/).length
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{post.title} | RunoBlog</title>
        <meta name="description" content={metaDescription} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={metaImageUrl} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImageUrl} />
      </Helmet>
      <Navigation />

      {/* Back Button */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <article className="container mx-auto px-4 py-12">
        <div className="mx-auto max-w-4xl">
          {/* Meta Info */}
          <div className="mb-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {post.categories && (
              <Badge variant="secondary" style={{ backgroundColor: post.categories.color }}>
                {post.categories.name}
              </Badge>
            )}
            {post.published_at && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {format(new Date(post.published_at), "MMM d, yyyy")}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.read_time}
            </span>
          </div>

          {/* Title */}
          <h1 className="mb-8 text-4xl font-bold tracking-tight md:text-5xl">
            {post.title}
          </h1>

          {/* Featured Image or Video */}
          {post.video_url ? (
            <YouTubeEmbed videoUrl={post.video_url} title={post.title} />
          ) : post.featured_image ? (
            <div className="mb-8 overflow-hidden rounded-lg">
              <img
                src={post.featured_image}
                alt={post.title}
                className="h-auto w-full object-cover"
              />
            </div>
          ) : null}

          {/* Tab Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="overview" className="gap-2">
                <FileText className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="premium" className="gap-2">
                <Lock className="h-4 w-4" />
                Unlock Premium Guide
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-8">
              <div className="prose prose-slate max-w-none dark:prose-invert">
                <div 
                  className="text-lg leading-relaxed text-muted-foreground whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
                />
              </div>
            </TabsContent>

            <TabsContent value="premium" className="mt-8 space-y-8">
              <PremiumUnlockGate
                purchaseType="premium_guide"
                entityId={post.id}
                title={`Unlock "${post.title}" Premium Guide`}
                description="Get the full 28-day learning roadmap and all downloadable resources for this article."
                perks={[
                  "Full 28-Day Roadmap with daily tasks & progress tracking",
                  "Every downloadable PDF & resource attached to this article",
                  "Yours forever — one-time $3 purchase",
                  "Or get All-Access for unlimited access to every premium guide",
                ]}
              >
                {/* 28-Day Roadmap */}
                <div id="premium-content-region">
                <div id="section-roadmap">
                  <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                    <Map className="h-6 w-6 text-primary" />
                    28-Day Roadmap
                  </h2>
                  {roadmap.length > 0 ? (
                    <div className="rounded-lg border border-border bg-card p-6 text-center">
                      <p className="text-muted-foreground mb-4">
                        Follow a structured 28-day journey to master this topic
                      </p>
                      <Button asChild size="lg">
                        <Link to={`/blog/${slug}/roadmap`}>View Full Roadmap</Link>
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-border bg-card p-8 text-center">
                      <p className="text-muted-foreground">
                        A learning roadmap hasn't been created for this article yet.
                      </p>
                    </div>
                  )}
                </div>

                {/* Downloadable Resources */}
                <div id="section-resources">
                  <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                    <Download className="h-6 w-6 text-primary" />
                    Downloadable Resources
                  </h2>
                  {premiumResources.length > 0 || post.resource_url ? (
                    <div className="space-y-3">
                      {premiumResources.map((r) => (
                        <div
                          key={r.id}
                          className="rounded-lg border border-border bg-card p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold">{r.name}</p>
                            {r.description && (
                              <p className="text-sm text-muted-foreground mt-1">{r.description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
                              {r.file_format && (
                                <span className="rounded bg-muted px-2 py-0.5">{r.file_format}</span>
                              )}
                              {r.file_size && <span>{r.file_size}</span>}
                            </div>
                          </div>
                          <Button asChild size="lg">
                            <a href={r.file_url} target="_blank" rel="noopener noreferrer" download>
                              <Download className="mr-2 h-4 w-4" />
                              Download
                            </a>
                          </Button>
                        </div>
                      ))}
                      {premiumResources.length === 0 && post.resource_url && (
                        <div className="rounded-lg border border-border bg-card p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                          <div>
                            <p className="font-semibold">{post.resource_name || "Premium Guide"}</p>
                            <p className="text-sm text-muted-foreground">Get the companion guide for this article</p>
                          </div>
                          <Button asChild size="lg">
                            <a href={post.resource_url} target="_blank" rel="noopener noreferrer" download>
                              <Download className="mr-2 h-4 w-4" />
                              Download
                            </a>
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-border bg-card p-8 text-center">
                      <p className="text-muted-foreground">
                        No downloadable resources have been attached to this article yet.
                      </p>
                    </div>
                  )}
                </div>

                {/* Quiz */}
                <div id="section-quiz">
                  <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                    <Brain className="h-6 w-6 text-primary" />
                    Knowledge Quiz
                  </h2>
                  {quizQuestions.length > 0 ? (
                    <Quiz questions={quizQuestions} quizId={quizId || ''} />
                  ) : (
                    <div className="rounded-lg border border-border bg-card p-8 text-center">
                      <p className="text-muted-foreground">
                        A quiz hasn't been created for this article yet.
                      </p>
                    </div>
                  )}
                </div>
                </div>
              </PremiumUnlockGate>
            </TabsContent>
          </Tabs>

          {/* Like and Share Section */}
          <div className="mt-12 border-t border-border pt-8">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="lg"
                onClick={handleLike}
                className="gap-2"
              >
                <Heart className="h-5 w-5" />
                Like ({likeCount})
              </Button>
              <h3 className="text-lg font-semibold">Share this article</h3>
              <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="lg" className="gap-2">
                    <Share2 className="h-4 w-4" />
                    Share
                  </Button>
                </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => handleShare('twitter')} className="gap-2 cursor-pointer">
                      <Twitter className="h-4 w-4" />
                      Share on Twitter
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('facebook')} className="gap-2 cursor-pointer">
                      <Facebook className="h-4 w-4" />
                      Share on Facebook
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('linkedin')} className="gap-2 cursor-pointer">
                      <Linkedin className="h-4 w-4" />
                      Share on LinkedIn
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleShare('copy')} className="gap-2 cursor-pointer">
                      {copied ? <Check className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
                      {copied ? 'Copied!' : 'Copy Link'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Related Articles Section */}
          {relatedPosts.length > 0 && (
            <div className="mt-12 border-t border-border pt-12">
              <h2 className="mb-8 text-2xl font-bold">Related Articles</h2>
              <div className="grid gap-6 md:grid-cols-3">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.slug}
                    to={`/blog/${relatedPost.slug}`}
                    className="group"
                  >
                    <div className="overflow-hidden rounded-lg border border-border bg-card transition-all hover:shadow-lg">
                      {relatedPost.featured_image && (
                        <div className="overflow-hidden">
                          <img
                            src={relatedPost.featured_image}
                            alt={relatedPost.title}
                            className="h-40 w-full object-cover transition-transform group-hover:scale-105"
                          />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                          {relatedPost.categories && (
                            <Badge variant="secondary" className="text-xs">
                              {relatedPost.categories.name}
                            </Badge>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {relatedPost.read_time}
                          </span>
                        </div>
                        <h3 className="line-clamp-2 font-semibold text-foreground group-hover:text-primary transition-colors">
                          {relatedPost.title}
                        </h3>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Newsletter CTA */}
      <section className="border-t border-border bg-muted/50">
        <div className="container mx-auto px-4 py-16">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold">Enjoyed this article?</h2>
            <p className="mb-8 text-muted-foreground">
              Subscribe to our newsletter and get the latest articles delivered directly to your inbox.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
              <input
                type="email"
                placeholder="Enter your email"
                className="rounded-lg border border-input bg-background px-4 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:w-80"
              />
              <Button>Subscribe</Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPost;
